package com.kanati.admin.api

import com.kanati.shared.admin.CreateOperatorRequest
import com.kanati.shared.admin.CurrentOperatorContextResponse
import com.kanati.shared.admin.LoginRequest
import com.kanati.shared.admin.LoginResponse
import com.kanati.shared.admin.ManagedOperatorSummary
import com.kanati.shared.admin.UpdateOperatorRequest
import com.kanati.shared.commerce.AdjustInventoryRequest
import com.kanati.shared.commerce.ConcessionOrderDto
import com.kanati.shared.commerce.ConcessionOrderSource
import com.kanati.shared.commerce.ConcessionOrderStatus
import com.kanati.shared.commerce.CreateProductRequest
import com.kanati.shared.commerce.InventoryResponse
import com.kanati.shared.commerce.ProductResponse
import com.kanati.shared.commerce.SetInventoryRequest
import com.kanati.shared.commerce.UpdateProductRequest
import com.kanati.shared.district.CreateDistrictRequest
import com.kanati.shared.district.District
import com.kanati.shared.district.DistrictSettingsResponse
import com.kanati.shared.district.UpdateDistrictSettingsRequest
import com.kanati.shared.report.DailySalesReportRow
import com.kanati.shared.report.InventoryReportRow
import com.kanati.shared.report.TopProductReportRow
import com.kanati.shared.school.CreateSchoolRequest
import com.kanati.shared.school.School
import com.kanati.shared.school.UpdateSchoolRequest
import com.kanati.shared.loyalty.AdjustLoyaltyPointsRequest
import com.kanati.shared.loyalty.CreateLoyaltyAccountRequest
import com.kanati.shared.loyalty.CreateLoyaltyRewardRequest
import com.kanati.shared.loyalty.LoyaltyAccountDto
import com.kanati.shared.loyalty.LoyaltyPointsLedgerEntryDto
import com.kanati.shared.loyalty.LoyaltyRedemptionRequest
import com.kanati.shared.loyalty.LoyaltyRedemptionResponse
import com.kanati.shared.loyalty.LoyaltyRewardDto
import com.kanati.shared.loyalty.UpdateLoyaltyRewardRequest
import com.kanati.shared.event.CreateEventRequest
import com.kanati.shared.event.EventDto
import com.kanati.shared.event.EventStatsDto
import com.kanati.shared.event.UpdateEventRequest
import com.kanati.shared.ticket.CreatePaperTicketBatchRequest
import com.kanati.shared.ticket.CreatePaperTicketBatchResponse
import com.kanati.shared.ticket.EventIssuedTicketsResponse
import com.kanati.shared.ticket.EventPaperTicketBatchesResponse
import com.kanati.shared.ticket.IssueTicketRequest
import com.kanati.shared.ticket.PaperTicketBatchDto
import com.kanati.shared.ticket.ReleasePaperTicketBatchRequest
import com.kanati.shared.ticket.ValidateTicketRequest
import com.kanati.shared.ticket.TicketValidationResponse
import com.kanati.shared.report.TicketSalesReportRow
import com.kanati.shared.report.TicketValidationActivityReportRow
import com.kanati.shared.consumer.ConsumerAppRuntimeConfigResponse
import com.kanati.admin.features.readiness.ReadinessAssessment
import com.kanati.admin.features.readiness.parseReadiness
import kotlinx.serialization.json.JsonObject
import com.kanati.shared.ticket.TicketDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import io.ktor.http.encodeURLPathPart
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

class AdminApiClient(
    baseUrl: String,
    private val httpClient: HttpClient = buildHttpClient(),
) {
    private val normalizedBaseUrl = baseUrl.trimEnd('/')
    var accessToken: String? = null

    val baseUrl: String get() = normalizedBaseUrl

    /** Backend list is active-only; terminal orders are available through scoped lookup. */
    suspend fun getActiveConcessionOrders(
        districtId: String, schoolId: String,
        status: ConcessionOrderStatus? = null, source: ConcessionOrderSource? = null,
    ): List<ConcessionOrderDto> = request {
        httpClient.get(url("/admin/concession-orders")) {
            concessionOrderParameters(districtId, schoolId)
            status?.let { parameter("status", it.name) }
            source?.let { parameter("source", it.name) }
        }
    }

    suspend fun getConcessionOrder(districtId: String, schoolId: String, orderId: String): ConcessionOrderDto = request {
        require(orderId.isNotBlank()) { "Order ID is required" }
        httpClient.get(url("/admin/concession-orders/${orderId.encodeURLPathPart()}")) {
            concessionOrderParameters(districtId, schoolId)
        }
    }

    suspend fun getConcessionOrderBySale(districtId: String, schoolId: String, saleId: String): ConcessionOrderDto = request {
        require(saleId.isNotBlank()) { "Sale ID is required" }
        httpClient.get(url("/admin/concession-orders/by-sale/${saleId.encodeURLPathPart()}")) {
            concessionOrderParameters(districtId, schoolId)
        }
    }

    private fun HttpRequestBuilder.concessionOrderParameters(districtId: String, schoolId: String) {
        require(districtId.isNotBlank() && schoolId.isNotBlank()) { "District and school are required" }
        applyAdminAuth()
        parameter("districtId", districtId)
        parameter("schoolId", schoolId)
    }

    suspend fun getGameNightReadiness(): ReadinessAssessment = parseReadiness(
        request<JsonObject>(includeAuth = false) { httpClient.get(url("/health/game-night")) }
    )

    suspend fun getConsumerRuntimeConfig(schoolId: String): ConsumerAppRuntimeConfigResponse =
        request(includeAuth = false) {
            httpClient.get(url("/consumer/schools/$schoolId/runtime-config"))
        }

    suspend fun getTicketSalesReport(
        districtId: String, schoolId: String, eventId: String? = null,
        from: String? = null, to: String? = null,
    ): List<TicketSalesReportRow> = request {
        httpClient.get(url("/admin/reports/ticket-sales")) {
            applyAdminAuth()
            ticketReportParameters(districtId, schoolId, eventId, from, to)
        }
    }

    suspend fun getTicketValidationActivityReport(
        districtId: String, schoolId: String, eventId: String? = null,
        from: String? = null, to: String? = null,
    ): List<TicketValidationActivityReportRow> = request {
        httpClient.get(url("/admin/reports/ticket-validation-activity")) {
            applyAdminAuth()
            ticketReportParameters(districtId, schoolId, eventId, from, to)
        }
    }

    private fun HttpRequestBuilder.ticketReportParameters(
        districtId: String, schoolId: String, eventId: String?, from: String?, to: String?,
    ) {
        parameter("districtId", districtId)
        parameter("schoolId", schoolId)
        eventId?.takeIf(String::isNotBlank)?.let { parameter("eventId", it) }
        from?.takeIf(String::isNotBlank)?.let { parameter("from", it) }
        to?.takeIf(String::isNotBlank)?.let { parameter("to", it) }
    }

    suspend fun login(request: LoginRequest): LoginResponse =
        request(includeAuth = false) {
            httpClient.post(url("/admin/auth/login")) {
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun logout() {
        request<Unit> {
            httpClient.post(url("/admin/auth/logout")) {
                applyAdminAuth()
            }
        }
    }

    suspend fun getCurrentOperatorContext(): CurrentOperatorContextResponse =
        request {
            httpClient.get(url("/admin/me")) {
                applyAdminAuth()
            }
        }

    suspend fun getSchools(districtId: String): List<School> =
        request {
            httpClient.get(url("/admin/schools")) {
                applyAdminAuth()
                parameter("districtId", districtId)
            }
        }

    suspend fun createSchool(request: CreateSchoolRequest): School =
        request {
            httpClient.post(url("/admin/schools")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun updateSchool(schoolId: String, request: UpdateSchoolRequest): School =
        request {
            httpClient.patch(url("/admin/schools/$schoolId")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getUsers(districtId: String): List<ManagedOperatorSummary> =
        request {
            httpClient.get(url("/admin/users")) {
                applyAdminAuth()
                parameter("districtId", districtId)
            }
        }

    suspend fun createUser(request: CreateOperatorRequest): ManagedOperatorSummary =
        request {
            httpClient.post(url("/admin/users")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun updateUser(operatorId: String, request: UpdateOperatorRequest): ManagedOperatorSummary =
        request {
            httpClient.patch(url("/admin/users/$operatorId")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getProducts(districtId: String, schoolId: String): List<ProductResponse> =
        request {
            httpClient.get(url("/admin/products")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
            }
        }

    suspend fun getDistricts(): List<District> =
        request {
            httpClient.get(url("/admin/districts")) {
                applyAdminAuth()
            }
        }

    suspend fun createDistrict(request: CreateDistrictRequest): District =
        request {
            httpClient.post(url("/admin/districts")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getDistrictSettings(districtId: String): DistrictSettingsResponse =
        request {
            httpClient.get(url("/admin/districts/$districtId/settings")) {
                applyAdminAuth()
            }
        }

    suspend fun updateDistrictSettings(
        districtId: String,
        request: UpdateDistrictSettingsRequest,
    ): DistrictSettingsResponse = request {
        httpClient.patch(url("/admin/districts/$districtId/settings")) {
            applyAdminAuth()
            header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
            setBody(request)
        }
    }

    suspend fun createProduct(request: CreateProductRequest): ProductResponse =
        request {
            httpClient.post(url("/admin/products")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun updateProduct(productId: String, request: UpdateProductRequest): ProductResponse =
        request {
            httpClient.patch(url("/admin/products/$productId")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getInventory(districtId: String, schoolId: String): List<InventoryResponse> =
        request {
            httpClient.get(url("/admin/inventory")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
            }
        }

    suspend fun adjustInventory(request: AdjustInventoryRequest): InventoryResponse =
        request {
            httpClient.post(url("/admin/inventory/adjust")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun setInventory(request: SetInventoryRequest): InventoryResponse =
        request {
            httpClient.post(url("/admin/inventory/set")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getEvents(districtId: String, schoolId: String): List<EventDto> =
        request {
            httpClient.get(url("/admin/events")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
            }
        }

    suspend fun createEvent(request: CreateEventRequest): EventDto =
        request {
            httpClient.post(url("/admin/events")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun updateEvent(eventId: String, request: UpdateEventRequest): EventDto =
        request {
            httpClient.patch(url("/admin/events/$eventId")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getEventStats(districtId: String, schoolId: String, eventId: String): EventStatsDto =
        request {
            httpClient.get(url("/admin/events/$eventId/stats")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
            }
        }

    suspend fun issueTicket(districtId: String, schoolId: String, eventId: String, request: IssueTicketRequest): TicketDto =
        request {
            httpClient.post(url("/admin/events/$eventId/tickets/issue")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getIssuedTickets(
        districtId: String,
        schoolId: String,
        eventId: String,
    ): EventIssuedTicketsResponse = request {
        httpClient.get(url("/admin/events/$eventId/tickets")) {
            applyAdminAuth()
            parameter("districtId", districtId)
            parameter("schoolId", schoolId)
        }
    }

    suspend fun createPaperTicketBatch(
        districtId: String,
        schoolId: String,
        eventId: String,
        request: CreatePaperTicketBatchRequest,
    ): CreatePaperTicketBatchResponse = request {
        httpClient.post(url("/admin/events/$eventId/tickets/paper-batches")) {
            applyAdminAuth()
            parameter("districtId", districtId)
            parameter("schoolId", schoolId)
            header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
            setBody(request)
        }
    }

    suspend fun getPaperTicketBatches(
        districtId: String,
        schoolId: String,
        eventId: String,
    ): EventPaperTicketBatchesResponse = request {
        httpClient.get(url("/admin/events/$eventId/paper-batches")) {
            applyAdminAuth()
            parameter("districtId", districtId)
            parameter("schoolId", schoolId)
        }
    }

    suspend fun releasePaperTicketBatch(
        districtId: String,
        schoolId: String,
        eventId: String,
        batchId: String,
        request: ReleasePaperTicketBatchRequest,
    ): PaperTicketBatchDto = request {
        httpClient.post(url("/admin/events/$eventId/paper-batches/$batchId/release")) {
            applyAdminAuth()
            parameter("districtId", districtId)
            parameter("schoolId", schoolId)
            header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
            setBody(request)
        }
    }

    suspend fun validateTicket(districtId: String, schoolId: String, eventId: String, request: ValidateTicketRequest): TicketValidationResponse =
        request {
            httpClient.post(url("/gate/events/$eventId/tickets/validate")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getLoyaltyAccounts(districtId: String, schoolId: String): List<LoyaltyAccountDto> =
        request {
            httpClient.get(url("/admin/loyalty/accounts")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
            }
        }

    suspend fun createLoyaltyAccount(request: CreateLoyaltyAccountRequest): LoyaltyAccountDto =
        request {
            httpClient.post(url("/admin/loyalty/accounts")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun getLoyaltyLedger(
        districtId: String,
        schoolId: String,
        accountId: String,
    ): List<LoyaltyPointsLedgerEntryDto> = request {
        httpClient.get(url("/admin/loyalty/accounts/$accountId/ledger")) {
            applyAdminAuth()
            parameter("districtId", districtId)
            parameter("schoolId", schoolId)
        }
    }

    suspend fun adjustLoyaltyPoints(
        accountId: String,
        request: AdjustLoyaltyPointsRequest,
    ): LoyaltyPointsLedgerEntryDto = request {
        httpClient.post(url("/admin/loyalty/accounts/$accountId/points")) {
            applyAdminAuth()
            header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
            setBody(request)
        }
    }

    suspend fun redeemLoyaltyReward(
        accountId: String,
        request: LoyaltyRedemptionRequest,
    ): LoyaltyRedemptionResponse = request {
        httpClient.post(url("/admin/loyalty/accounts/$accountId/redemptions")) {
            applyAdminAuth()
            header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
            setBody(request)
        }
    }

    suspend fun getLoyaltyRewards(districtId: String, schoolId: String): List<LoyaltyRewardDto> =
        request {
            httpClient.get(url("/admin/loyalty/rewards")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
            }
        }

    suspend fun createLoyaltyReward(request: CreateLoyaltyRewardRequest): LoyaltyRewardDto =
        request {
            httpClient.post(url("/admin/loyalty/rewards")) {
                applyAdminAuth()
                header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
                setBody(request)
            }
        }

    suspend fun updateLoyaltyReward(
        rewardId: String,
        request: UpdateLoyaltyRewardRequest,
    ): LoyaltyRewardDto = request {
        httpClient.patch(url("/admin/loyalty/rewards/$rewardId")) {
            applyAdminAuth()
            header(HttpHeaders.ContentType, ContentType.Application.Json.toString())
            setBody(request)
        }
    }

    suspend fun getReports(
        districtId: String,
        schoolId: String,
        from: String? = null,
        to: String? = null,
    ): List<DailySalesReportRow> = request {
        httpClient.get(url("/admin/reports/daily-sales")) {
            applyAdminAuth()
            parameter("districtId", districtId)
            parameter("schoolId", schoolId)
            from?.takeIf(String::isNotBlank)?.let { parameter("from", it) }
            to?.takeIf(String::isNotBlank)?.let { parameter("to", it) }
        }
    }

    suspend fun getTopProductsReport(
        districtId: String,
        schoolId: String,
        from: String,
        to: String = from,
        limit: Int = 200,
    ): List<TopProductReportRow> = request {
        httpClient.get(url("/admin/reports/top-products")) {
            applyAdminAuth()
            parameter("districtId", districtId)
            parameter("schoolId", schoolId)
            parameter("from", from)
            parameter("to", to)
            parameter("limit", limit)
        }
    }

    suspend fun getInventoryOnHand(districtId: String, schoolId: String): List<InventoryReportRow> =
        request {
            httpClient.get(url("/admin/reports/inventory-on-hand")) {
                applyAdminAuth()
                parameter("districtId", districtId)
                parameter("schoolId", schoolId)
            }
        }

    private fun url(path: String): String = "$normalizedBaseUrl$path"

    private suspend inline fun <reified T> request(
        includeAuth: Boolean = true,
        crossinline block: suspend () -> HttpResponse,
    ): T {
        val response = block()
        if (!response.status.isSuccess()) {
            val details = response.bodyAsText()
            throw AdminApiException(
                formatErrorMessage(response = response, details = details),
                response.status.value,
            )
        }
        return response.body()
    }

    private fun HttpRequestBuilder.applyAdminAuth() {
        accessToken?.takeIf(String::isNotBlank)?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun formatErrorMessage(response: HttpResponse, details: String): String {
        val trimmed = details.trim()
        if (trimmed.isBlank()) {
            if (response.status.value == 401) {
                return "Your admin session is not available. Sign in again."
            }
            if (response.status.value == 403) {
                return "You do not have access to that district or admin action."
            }
            return "Request failed with HTTP ${response.status.value}"
        }

        if (trimmed.startsWith("{")) {
            runCatching {
                val jsonObject = Json.parseToJsonElement(trimmed).jsonObject
                jsonObject["error"]?.jsonPrimitive?.contentOrNull
                    ?: jsonObject["message"]?.jsonPrimitive?.contentOrNull
            }.getOrNull()?.takeIf(String::isNotBlank)?.let { return it }
        }

        if (trimmed.contains("Cannot ") && trimmed.contains("/api/")) {
            return "Local dev routing is out of date. Restart ./gradlew jsBrowserDevelopmentRun and refresh the browser."
        }

        if (trimmed.contains("<html", ignoreCase = true)) {
            val routeMessage = Regex(
                pattern = "(?is)<pre>(.*?)</pre>",
            ).find(trimmed)?.groups?.get(1)?.value?.trim()

            return routeMessage ?: "Request failed with HTTP ${response.status.value}"
        }

        return trimmed
    }

    private companion object {
        fun buildHttpClient(): HttpClient = HttpClient {
            expectSuccess = false
            install(ContentNegotiation) {
                json(
                    Json {
                        ignoreUnknownKeys = true
                        explicitNulls = false
                    }
                )
            }
        }
    }
}

class AdminApiException(
    message: String,
    val statusCode: Int? = null,
) : Exception(message)
